import { Cancel, CreditCard, DoneAll, Print } from '@mui/icons-material'
import { Box, Button } from '@mui/material'
import CustomButton from 'components/Fileds/CustomButton'
import React, { useState } from 'react'
import "./style.css";
import ConfirmationDialog from 'components/Modal/ConfirmationDialog/ConfirmationDialog';
import { useChangeOrderStatusMutation, useCollectOrderPaymentMutation, useCompleteOrderPaymentMutation, useLazyGetPrintReceiptQuery } from 'pages/orders/services';
import { useNavigate } from 'react-router-dom';
import CollectOrderPayment from 'components/Modal/CollectOrderPayment';

const ButtonActionsBar = ({ orderDetails, onOpenSplitModal }) => {
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    // Action type for dialog (extendable in future if needed)
    const [confirmAction, setConfirmAction] = useState(null);
    const [openPaymentModal, setOpenPaymentModal] = useState(false); // ✅ state for payment modal
    const [paymentModeOrder, setPaymentModeOrder] = useState('cash'); // State to hold selected payment mode

    const [changeStatusApi, { isLoading: isUpdating }] = useChangeOrderStatusMutation();
    const [collectPaymentApi, { isLoading: isCollecting }] = useCollectOrderPaymentMutation();
    const [completeOrderPaymentApi, { isLoading: isCompleting }] = useCompleteOrderPaymentMutation();
    const [triggerPrint, { isFetching: isPrinting }] = useLazyGetPrintReceiptQuery();

    const handleCancelOrder = () => {
        setConfirmAction("cancel");
        setIsConfirmOpen(true);
    };

    const handleConfirmAction = async () => {
        try {
            setIsLoading(true);
            const id = orderDetails?.id;
            if (confirmAction === "cancel") {
                const body = { order_status: "cancel" }
                const response = await changeStatusApi({ id, body })
                if (response?.data?.status == 200) {
                    navigate("/tables");
                }
            }
            if (confirmAction === "complete") {
                const alreadyPaid = orderDetails?.paid_amount || 0;
                const totalAmount = orderDetails?.total_amount || 0;
                const remainingAmount = totalAmount - alreadyPaid;

                const payload = {
                    id: orderDetails?.id,
                    paid_amount: remainingAmount > 0 ? parseFloat(remainingAmount).toFixed(2) : parseFloat(totalAmount).toFixed(2),
                    payment_mode: paymentModeOrder
                };

                const response = await completeOrderPaymentApi(payload);
                if (response?.data?.status == 200) {
                    navigate("/tables");
                }
            }
        } catch (error) {
            console.error("Failed to cancel order:", error);
        } finally {
            setIsLoading(false);
            setIsConfirmOpen(false);
        }
    };

    const getBtnText = (action) => {
        if (action === "cancel") return "Cancel Order";
        if (action === "complete") return "Complete Order";
        return "Confirm";
    };

    const getInfoText = (action) => {
        if (action === "cancel") return "Are you sure you want to cancel this order?";
        if (action === "complete") return "Are you sure you want to complete this order?";
        return "Are you sure?";
    };

    const handleCollectPayment = async (paymentMode) => {
        // calculate remaining payment
        const alreadyPaid = orderDetails?.paid_amount || 0;
        const totalAmount = orderDetails?.total_amount || 0;
        const remainingAmount = totalAmount - alreadyPaid;

        const payload = {
            id: orderDetails?.id,
            paid_amount: remainingAmount > 0 ? parseFloat(remainingAmount).toFixed(2) : parseFloat(totalAmount).toFixed(2),
            payment_mode: paymentMode
        };
        try {
            const res = await collectPaymentApi(payload);
            if (res?.data?.status == 200) {
                setOpenPaymentModal(false);
            }
        } catch (error) {
            console.log("Error in Collect Payment", error)
        }
    }

    const handleCompleteOrder = () => {
        const alreadyPaid = orderDetails?.paid_amount || 0;
        const totalAmount = orderDetails?.total_amount || 0;
        if (orderDetails?.payment_status !== "paid" || alreadyPaid < totalAmount) {
            setOpenPaymentModal(true);
        } else {
            setConfirmAction("complete");
            setIsConfirmOpen(true);
        }
    };


    const handleCollectPaymentAndComplete = async (paymentMode) => {
        const alreadyPaid = orderDetails?.paid_amount || 0;
        const totalAmount = orderDetails?.total_amount || 0;
        setPaymentModeOrder(paymentMode);
        // if (orderDetails?.payment_status !== "paid" || alreadyPaid < totalAmount) {
        //     setOpenPaymentModal(true);
        // } else {
        setConfirmAction("complete");
        setIsConfirmOpen(true);
        // }
    };

    const handlePrintReceipt = async () => {
        try {
            const isCustomer = orderDetails?.order_source === 'customer';
            const platformFeeValue = isCustomer ? parseFloat(orderDetails?.platform_fee || 0) : 0;
            const platformFeePercent = isCustomer
                ? parseFloat(orderDetails?.marketplace_fee_percentage ?? 0)
                : 0;
            const res = await triggerPrint({
                id: orderDetails?.id,
                order_source: orderDetails?.order_source || (orderDetails?.customer_id ? 'customer' : 'pos'),
                platform_fee: platformFeeValue,
                platform_fee_percent: platformFeePercent
            }).unwrap();
            if (res?.data?.html) {
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    printWindow.document.write(res.data.html);
                    printWindow.document.close();
                    setTimeout(() => {
                        printWindow.print();
                        printWindow.close();
                    }, 500);
                }
            }
        } catch (error) {
            console.error("Failed to fetch print receipt:", error);
        }
    };

    const isSplitAllowed = orderDetails?.order_type === 'dine_in' || orderDetails?.order_type === 'booking';
    const isPaid = orderDetails?.payment_status === 'paid';

    return (
        <Box className='d-flex align-items-center button-action-container' sx={{ gap: 3, justifyContent: 'flex-end' }}>
            {orderDetails?.orderItems?.length > 0 ?
                <>
                    <CustomButton
                        startIcon={<Print />}
                        sx={{ backgroundColor: '#04ADB3' }}
                        className='action-order-btn'
                        onClick={handlePrintReceipt}
                        disabled={isPrinting}
                    >
                        Print Receipt
                    </CustomButton>
                    {
                        !isPaid &&
                        <CustomButton
                            startIcon={<CreditCard />}
                            sx={{ backgroundColor: '#1361D5' }}
                            className='action-order-btn'
                            onClick={() => setOpenPaymentModal(true)}
                        >
                            Collect Payment
                        </CustomButton>
                    }
                    {isSplitAllowed && !isPaid && onOpenSplitModal && (
                        <CustomButton
                            startIcon={<CreditCard />}
                            sx={{ backgroundColor: '#EAB308', color: '#fff', '&:hover': { backgroundColor: '#D97706' } }}
                            className='action-order-btn'
                            onClick={onOpenSplitModal}
                        >
                            Split Bill
                        </CustomButton>
                    )}
                    {
                        isPaid &&
                        <CustomButton
                            startIcon={<DoneAll />}
                            sx={{ backgroundColor: '#2E7D32' }}
                            className='action-order-btn'
                            disabled={isLoading || isUpdating || isCompleting}
                            onClick={handleCompleteOrder}
                        >
                            Complete Order
                        </CustomButton>
                    }
                </>
                :
                <CustomButton
                    startIcon={<Cancel />}
                    sx={{ backgroundColor: '#D41616' }}
                    className='action-order-btn'
                    disabled={isLoading || isUpdating || isCompleting}
                    onClick={handleCancelOrder}
                >
                    Cancel Order
                </CustomButton>
            }

            {isConfirmOpen &&
                <ConfirmationDialog
                    open={isConfirmOpen}
                    handleClose={() => setIsConfirmOpen(false)}
                    handleConfirm={handleConfirmAction}
                    btnText={getBtnText(confirmAction)}
                    infoText={getInfoText(confirmAction)}
                    isLoading={isLoading || isUpdating || isCompleting}
                    action={confirmAction}
                />
            }

            {/* ✅ Collect Payment Modal */}
            <CollectOrderPayment
                open={openPaymentModal}
                onClose={() => setOpenPaymentModal(false)}
                orderDetails={orderDetails}
                orderItemTotal={orderDetails?.order_items_total}
                // onApplyDiscount={() => setOpenPaymentModal(false)}
                onCollectPayment={handleCollectPayment}
                onCollectAndComplete={handleCollectPaymentAndComplete}
                isCollecting={isCollecting}
                isCompleting={isCompleting}
            />
        </Box>
    )
}

export default ButtonActionsBar
